import type { Dayjs } from '@/config/dayjs';
import type {
  AccountHistoricalComparisonItem,
  AccountHistoricalTrendResponse,
} from '@/models/AccountViewModel';
import {
  buildWealthTrendChart,
  type WealthTrendChart,
} from '@/modules/advisor/wealthTrend/wealthTrendChartData';

export type HistoricalTrendRecord =
  AccountHistoricalTrendResponse['Records'][number];

export interface HistoricalTrendSeriesInput {
  name: string;
  data: HistoricalTrendRecord['data'];
  absolute?: boolean;
}

const UNSET_GROUP = 'Not Set';

const UNGROUPED_SERIES = 'Account Balances';

function isTagGrouping(groupBy: string): boolean {
  return groupBy.startsWith('investor:') || groupBy.startsWith('advisor:');
}

export interface TrendGroupFallbackLabels {
  noInstitution: string;
  notSet: string;
}

export function trendGroupNameFor(
  groupBy: string,
  name: string,
  labels: TrendGroupFallbackLabels,
): string {
  if (name !== UNSET_GROUP && name !== '') return name;
  return groupBy === 'institution' ? labels.noInstitution : labels.notSet;
}

export interface HistoricalTrendGroupConfig {
  valueProperty: string;
  displayProperty: string;
}

export function trendGroupConfigFor(
  groupBy: string,
): HistoricalTrendGroupConfig {
  if (isTagGrouping(groupBy)) {
    return { valueProperty: groupBy, displayProperty: groupBy };
  }
  if (groupBy === 'institution') {
    return {
      valueProperty: 'InstitutionId',
      displayProperty: 'InstitutionName',
    };
  }
  return {
    valueProperty: 'ClassificationId',
    displayProperty: 'ClassificationName',
  };
}

function carryLastKnownValue(
  values: readonly (number | null)[],
): (number | null)[] {
  let last: number | null = null;
  return values.map((value) => {
    if (value !== null) last = value;
    return last;
  });
}

function sumKnownValues(
  rows: readonly (readonly (number | null)[])[],
  dayCount: number,
): (number | null)[] {
  return Array.from({ length: dayCount }, (_unused, day) => {
    const known = rows
      .map((values) => values[day])
      .filter((value): value is number => value !== null);
    return known.length > 0
      ? known.reduce((sum, value) => sum + value, 0)
      : null;
  });
}

// Same-name series are summed: the grid groups its rows by name, not by id.
export function buildHistoricalTrendChart(
  series: readonly HistoricalTrendSeriesInput[],
): WealthTrendChart {
  const chart = buildWealthTrendChart(
    series.map((line, index) => ({
      id: index + 1,
      name: line.name,
      data: line.data,
    })),
  );

  const byName = new Map<
    string,
    { absolute: boolean; rows: (number | null)[][] }
  >();
  chart.lines.forEach((line, index) => {
    const carried = carryLastKnownValue(line.values);
    const entry = byName.get(line.name);
    if (entry) {
      entry.rows.push(carried);
    } else {
      byName.set(line.name, {
        absolute: series[index].absolute ?? false,
        rows: [carried],
      });
    }
  });

  return {
    days: chart.days,
    lines: [...byName.entries()].map(([name, { absolute, rows }], index) => ({
      id: index + 1,
      name,
      values: sumKnownValues(rows, chart.days.length).map((value) =>
        value !== null && absolute ? Math.abs(value) : value,
      ),
    })),
  };
}

export type HistoricalTrendEndingAccount = Pick<
  AccountHistoricalComparisonItem,
  'IsAsset' | 'GroupName' | 'InstitutionName'
>;

export function endingTrendSeriesName(
  trendType: string,
  groupConfig: HistoricalTrendGroupConfig | undefined,
  account: HistoricalTrendEndingAccount,
): string | null {
  if (trendType === 'deposits') {
    return account.IsAsset ? UNGROUPED_SERIES : null;
  }
  if (trendType === 'liabilities') {
    return account.IsAsset ? null : UNGROUPED_SERIES;
  }
  if (trendType !== 'grouped' || !groupConfig) return null;
  if (groupConfig.displayProperty === 'InstitutionName') {
    return account.InstitutionName || UNSET_GROUP;
  }
  return account.GroupName || UNSET_GROUP;
}

export interface HistoricalTrendEndingBalance {
  name: string;
  balance: number;
  absolute?: boolean;
}

export function withLiveEndpoint(
  chart: WealthTrendChart,
  endDay: Dayjs,
  balances: readonly HistoricalTrendEndingBalance[],
): WealthTrendChart {
  const totals = new Map<string, { sum: number; absolute: boolean }>();
  for (const { name, balance, absolute } of balances) {
    const total = totals.get(name);
    if (total) {
      total.sum += balance;
    } else {
      totals.set(name, { sum: balance, absolute: absolute ?? false });
    }
  }

  const endingValue = ({
    sum,
    absolute,
  }: {
    sum: number;
    absolute: boolean;
  }) => (absolute ? Math.abs(sum) : sum);

  const liveOnlyLines = (
    plotted: ReadonlySet<string>,
    dayCount: number,
    firstId: number,
  ) =>
    [...totals.entries()]
      .filter(([name]) => !plotted.has(name))
      .map(([name, total], index) => ({
        id: firstId + index,
        name,
        values: [
          ...Array.from({ length: dayCount - 1 }, () => null),
          endingValue(total),
        ],
      }));

  if (totals.size === 0) return chart;
  if (chart.days.length === 0) {
    return { days: [endDay], lines: liveOnlyLines(new Set(), 1, 1) };
  }

  const lastDay = chart.days[chart.days.length - 1];
  if (lastDay.isAfter(endDay, 'day')) return chart;

  const replacesLastDay = lastDay.isSame(endDay, 'day');
  const days = replacesLastDay ? chart.days : [...chart.days, endDay];
  const plotted = new Set(chart.lines.map((line) => line.name));
  return {
    days,
    lines: [
      ...chart.lines.map((line) => {
        const total = totals.get(line.name);
        const endValue = total
          ? endingValue(total)
          : line.values[line.values.length - 1];
        const kept = replacesLastDay ? line.values.slice(0, -1) : line.values;
        return { ...line, values: [...kept, endValue] };
      }),
      ...liveOnlyLines(plotted, days.length, chart.lines.length + 1),
    ],
  };
}
